/**
 * CSV / Excel export of the Forecast grid.
 *
 * Mirrors the table-data params so the file reflects the on-screen filters
 * and the view-context picker (group / company / site). Columns follow the
 * same customer switches the grid does — decided server-side.
 */
import { useState } from 'react';
import { purchasingAPI } from '../../../../lib/api';
import { toast } from 'sonner';
import { ForecastFilters } from './forecastGridTypes';

type ViewParams = Record<string, number | undefined>;

/**
 * Downloads the Forecast export for the applied filters.
 *
 * @param appliedFilters - the filters the grid is currently showing
 * @param viewLevel - the top-bar view level
 * @param getViewQueryParams - the top-bar group / company / site ids
 * @returns `exporting` and `handleExport(format)`
 * @example
 * const { exporting, handleExport } = useForecastExport(appliedFilters, viewLevel, getViewQueryParams);
 */
export function useForecastExport(
  appliedFilters: ForecastFilters,
  viewLevel: string,
  getViewQueryParams: () => ViewParams,
) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async (fileFormat: 'csv' | 'xlsx' = 'csv') => {
    try {
      setExporting(true);
      const viewParams = getViewQueryParams();
      const response = await purchasingAPI.exportForecasts({
        file_format: fileFormat,
        part_number: appliedFilters.partNumber || undefined,
        po_search: appliedFilters.poNumber || undefined,
        part_search: appliedFilters.partSearch || undefined,
        supplier_part_number: appliedFilters.supplierPartNumber || undefined,
        commodity_group: appliedFilters.commodityGroup || undefined,
        commodity_type: appliedFilters.commodityType || undefined,
        supplier_group: appliedFilters.supplierGroup || undefined,
        supplier_id: appliedFilters.supplierId ? parseInt(appliedFilters.supplierId) : undefined,
        customer_id: appliedFilters.customerId ? parseInt(appliedFilters.customerId) : undefined,
        record_type_filter: appliedFilters.recordType ? parseInt(appliedFilters.recordType) : undefined,
        import_date_from: appliedFilters.importDateFrom || undefined,
        import_date_to: appliedFilters.importDateTo || undefined,
        // CR-6448: keep the file in step with the supplier panel's filters
        import_date: appliedFilters.importDate || undefined,
        warehouse_code: appliedFilters.warehouse || undefined,
        // CR-6440 rework: Supplier Code reaches the file too.
        supplier_code: appliedFilters.supplierCode || undefined,
        // CR-6440: the customer panel's Company / Supplier section filters.
        // They narrowed the grid (once the backend read them) but never the
        // file — an export with more rows than the screen leaves the building
        // unquestioned.
        company_country: appliedFilters.companyCountry || undefined,
        division: appliedFilters.division ? parseInt(appliedFilters.division) : undefined,
        supplier_country: appliedFilters.supplierCountry || undefined,
        view_level: viewLevel,
        group: viewParams['group'] !== undefined ? Number(viewParams['group']) : undefined,
        company: viewParams['company'] !== undefined ? Number(viewParams['company']) : undefined,
        // The panel's Site filter overrides the picker's, exactly as
        // buildForecastTableParams does for the grid.
        site: appliedFilters.site
          ? parseInt(appliedFilters.site)
          : (viewParams['site'] !== undefined ? Number(viewParams['site']) : undefined),
      });

      const blob = new Blob([response.data], {
        type: fileFormat === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `forecast_export_${new Date().toISOString().split('T')[0]}.${fileFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Forecast data exported successfully');
    } catch (err: unknown) {
      console.error('Export error:', err);
      toast.error('Failed to export forecast data');
    } finally {
      setExporting(false);
    }
  };

  return { exporting, handleExport };
}
